import { useLazyQuery } from "@vue/apollo-composable";
import {
    generateRelatedActivitiesQuery,
    getActivitiesQuery,
    RelatedActivitiesContext,
} from "@/abilities/activities/graphql/activity.query";
import { computed, ref, watch, watchEffect } from "vue";
import { generateQueryFilterForSearch } from "@/shared/facets/facets-filter-generator";
import { SearchParams } from "@/shared/search/search.types";
import { getDataQueryLanguages } from "@/shared/services/providers/language-provider";
import { useSearchConfig, DataSearchConfigId } from "@/shared/search/search-config";
import { useDataDisplayConfig } from "@/shared/data-display-config/composable/data-display-config";
import { DataDisplayConfigId } from "@/shared/data-display-config/composable/data-display.model";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { AccessFeature } from "@/shared/access-control/access-control.ts";
import { mapActivityEdgeToActivityResult } from "@/shared/activities/mapper/activity.mapper.ts";
import { ActivityEdge } from "@/shared/services/graphql/generated/admin-graph-types.ts";
import { useI18n } from "vue-i18n";
import { INIT_LOAD_AMOUNT } from "@/shared/environment/consts.ts";

export const useActivitiesSearch = ({
    selected,
    searchPhrase,
    sort,
    searchContext,
    count,
    showFirstRelevantPropertyItemOnly,
}: SearchParams) => {
    const context = ref<RelatedActivitiesContext>();
    const { t } = useI18n();
    const { hasAccess: fuzzy } = useHasAccess({ featureID: AccessFeature.fuzzy_search });

    watch(
        () => searchContext?.value,
        () => {
            context.value = undefined;
            if (searchContext?.value?.mechanic) context.value = "mechanic";
            else if (searchContext?.value?.asset) context.value = "asset";
            else if (searchContext?.value?.article) context.value = "article";
            else if (searchContext?.value?.product) context.value = "product";
        },
        { immediate: true }
    );

    //first fetch search configuration for activities selection
    const { result: activitiesSearchConfigResult, loading: activitiesSearchConfigLoading } = useSearchConfig(
        DataSearchConfigId.activitiesSelection
    );

    const variables = computed(() => {
        const { contentFilter } = generateQueryFilterForSearch(
            selected?.value ?? [],
            fuzzy.value,
            searchContext?.value,
            activitiesSearchConfigResult.value?.searchEntries,
            searchPhrase?.value
        );

        const variables = {
            filter: contentFilter,
            acceptedLanguages: getDataQueryLanguages(),
            first: count ?? INIT_LOAD_AMOUNT,
            after: "",
            sort: sort?.value,
        };

        if (!searchContext?.value) return variables;

        let id;

        if (searchContext?.value?.mechanic) id = searchContext.value.mechanic;
        else if (searchContext?.value?.asset && searchContext.value.product)
            id = `${searchContext.value.product}_${searchContext.value.asset}`;
        else if (searchContext?.value.article) id = searchContext.value.article;
        else if (searchContext?.value.product) id = searchContext.value.product;

        return { id, ...variables };
    });

    //first fetch display configuration for activities selection
    const {
        result: dataDisplayResult,
        loading: dataConfigLoading,
        getDataDisplayPropertiesByConfig,
    } = useDataDisplayConfig(DataDisplayConfigId.activitiesSelection, t);

    const query = computed(() => {
        if (!context.value)
            return getActivitiesQuery(
                dataDisplayResult.value?.datafieldsFragment.datafields ?? {},
                dataDisplayResult.value?.datafieldsFragment.localizationDatafields ?? {}
            );

        return generateRelatedActivitiesQuery(
            context.value,
            dataDisplayResult.value?.datafieldsFragment.datafields ?? {},
            dataDisplayResult.value?.datafieldsFragment.localizationDatafields ?? {}
        );
    });

    const {
        loading: searchLoading,
        error: searchError,
        result: lazyQueryResult,
        fetchMore,
        load,
    } = useLazyQuery(query, variables);

    const stop = watchEffect(
        () => {
            // Continue if activitiesSearchConfigResult is available and
            // "sort" is optional and if "sort" is provided, it must have a defined value.
            // Initially, sort.value may be undefined until user settings are loaded.
            // Once settings are loaded, sort.value will be defined and the function can proceed.
            if (!activitiesSearchConfigResult.value || (sort && !sort.value)) return;

            load();
            stop();
        },
        { flush: "post" }
    );

    const loading = computed(
        () =>
            (sort && !sort.value) ||
            searchLoading.value ||
            activitiesSearchConfigLoading.value ||
            dataConfigLoading.value
    );

    const error = computed(() => {
        if (searchError.value) console.error(searchError.value);
        return searchError.value;
    });

    const result = computed(() => {
        if (!lazyQueryResult.value) return null;

        let activities = [];
        let total;

        if (context.value && lazyQueryResult.value[context.value]) {
            activities = lazyQueryResult.value[context.value].relatedActivities.activities;
            total = lazyQueryResult.value[context.value].relatedActivities.total;
        } else if (lazyQueryResult.value.activities) {
            activities = lazyQueryResult.value.activities.activities;
            total = lazyQueryResult.value.activities.total;
        }

        return {
            activities,
            total,
        };
    });

    const activities = computed(() => {
        return result.value?.activities
            ? result.value?.activities.map((x: ActivityEdge) => {
                  const activityResult = mapActivityEdgeToActivityResult(x, getDataDisplayPropertiesByConfig);

                  if (showFirstRelevantPropertyItemOnly && activityResult.propertyItems.length > 0) {
                      //Only one metadata item should be displayed:
                      //the first priority property item, otherwise the first property item
                      const firstPriorityElement = activityResult.propertyItems.find((item) => item.isPriority);
                      if (firstPriorityElement) activityResult.propertyItems = [firstPriorityElement];
                      else activityResult.propertyItems = [activityResult.propertyItems[0]];
                  }

                  return activityResult;
              })
            : undefined;
    });

    const total = computed(() => {
        return result.value?.total;
    });

    const searchFields = computed(() => {
        return activitiesSearchConfigResult.value?.searchEntries ?? [];
    });

    return {
        result,
        activities,
        total,
        searchFields,
        loading,
        error,
        fetchMore,
    };
};
